import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { ArrowUpRight, Zap, Camera, Layers, Palette, Globe, Clock, CheckCircle, ArrowRight, Sparkles } from 'lucide-react';

gsap.registerPlugin(ScrollTrigger);

const ShotLab = () => {
  const [sliderPosition, setSliderPosition] = useState(50);
  const heroRef = useRef(null);

  useEffect(() => {
    document.title = 'ShotLab | AI Product Photography | Squarex Studios';

    const ctx = gsap.context(() => {
      gsap.fromTo('.shotlab-hero-title', { opacity: 0, y: 100 }, { opacity: 1, y: 0, duration: 1.2, ease: 'power4.out' });
      gsap.fromTo('.shotlab-hero-subtitle', { opacity: 0, y: 50 }, { opacity: 1, y: 0, duration: 1, delay: 0.3, ease: 'power3.out' });

      const cards = document.querySelectorAll('.capability-card');
      cards.forEach((card) => {
        gsap.fromTo(card, { opacity: 0, y: 50, scale: 0.95 }, {
          opacity: 1, y: 0, scale: 1, duration: 0.6, ease: 'power3.out',
          scrollTrigger: { trigger: card, start: 'top 85%' },
        });
      });
    });
    return () => ctx.revert();
  }, []);

  const handleSliderChange = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percentage = Math.min(Math.max((x / rect.width) * 100, 0), 100);
    setSliderPosition(percentage);
  };

  return (
    <div data-testid="shotlab-page">
      {/* Hero Section */}
      <section ref={heroRef} className="relative min-h-screen flex items-center pt-24 pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#af0000]/10 via-[#0F0F0F] to-[#0F0F0F]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(175,0,0,0.15),transparent_60%)]" />

        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-[#af0000]/20 text-[#af0000] px-4 py-2 rounded-full text-sm font-semibold mb-8">
                <Zap size={16} /> AI-Powered Visual Production
              </div>
              <h1 className="shotlab-hero-title text-5xl md:text-6xl lg:text-7xl font-bold font-['Syne'] tracking-tighter leading-[0.95] mb-6">
                SQRX<br /><span className="text-[#af0000]">ShotLab</span>
              </h1>
              <p className="shotlab-hero-subtitle text-2xl md:text-3xl text-[#94a3b8] font-light mb-6">
                AI Product Photo Shoot & Visual Production Lab
              </p>
              <p className="shotlab-hero-subtitle text-[#94a3b8] text-lg leading-relaxed mb-10 max-w-xl">
                Revolutionary product visualization that creates studio-quality, lifestyle, and brand-aligned imagery using advanced generative AI systems. No physical shoot required.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/contact" data-testid="request-sample-btn" className="btn-primary flex items-center justify-center gap-2">
                  Request Visual Sample <Sparkles size={18} />
                </Link>
                <Link to="/contact" data-testid="start-ai-shoot-btn" className="btn-secondary flex items-center justify-center gap-2">
                  Start AI Shoot <ArrowUpRight size={18} />
                </Link>
              </div>
            </div>

            <div className="relative">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-4">
                  <div className="aspect-[3/4] overflow-hidden bg-[#1a1a1a]">
                    <img src="https://images.unsplash.com/photo-1523275335684-37898b6baf30?w=400&h=533&fit=crop" alt="AI Product Shot" className="w-full h-full object-cover" />
                  </div>
                  <div className="aspect-square overflow-hidden bg-[#1a1a1a]">
                    <img src="https://images.unsplash.com/photo-1505740420928-5e560c06d30e?w=400&h=400&fit=crop" alt="AI Product Shot" className="w-full h-full object-cover" />
                  </div>
                </div>
                <div className="space-y-4 pt-12">
                  <div className="aspect-square overflow-hidden bg-[#1a1a1a]">
                    <img src="https://images.unsplash.com/photo-1542291026-7eec264c27ff?w=400&h=400&fit=crop" alt="AI Product Shot" className="w-full h-full object-cover" />
                  </div>
                  <div className="aspect-[3/4] overflow-hidden bg-[#1a1a1a]">
                    <img src="https://images.unsplash.com/photo-1526170375885-4d8ecf77b99f?w=400&h=533&fit=crop" alt="AI Product Shot" className="w-full h-full object-cover" />
                  </div>
                </div>
              </div>
              <div className="absolute -bottom-6 -left-6 w-48 h-48 border-2 border-[#af0000] -z-10" />
            </div>
          </div>
        </div>
      </section>

      {/* What is ShotLab */}
      <section className="py-24 md:py-32 bg-[#141414]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-3xl mx-auto text-center">
            <p className="text-[#af0000] font-mono text-sm uppercase tracking-widest mb-4">The Future of Product Photography</p>
            <h2 className="text-4xl md:text-5xl font-bold font-['Syne'] tracking-tight mb-8">What is SQRX ShotLab?</h2>
            <p className="text-xl text-[#94a3b8] leading-relaxed">
              SQRX ShotLab is an AI-driven product visualization service that creates studio-quality, lifestyle, and brand-aligned product imagery using advanced generative systems. We eliminate the need for expensive photo shoots, long production timelines, and logistical headaches — delivering premium visuals in a fraction of the time and cost.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mt-16">
            <StatCard value="70%" label="Cost Savings" />
            <StatCard value="2 Weeks" label="Avg. Delivery" />
            <StatCard value="50+" label="Images Per Project" />
            <StatCard value="∞" label="Revisions" />
          </div>
        </div>
      </section>

      {/* Capabilities */}
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <p className="text-[#af0000] font-mono text-sm uppercase tracking-widest mb-4">Capabilities</p>
            <h2 className="text-4xl md:text-5xl font-bold font-['Syne'] tracking-tight">What we can create</h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <CapabilityCard icon={Camera} title="Studio Product Shoots" description="Clean backgrounds, controlled lighting, and premium shadows. Perfect product photography without a physical studio." features={['Infinite white backgrounds', 'Shadow control', 'Multiple angles', 'Batch processing']} />
            <CapabilityCard icon={Layers} title="Lifestyle Visuals" description="Place your products in real-world contexts. From kitchen countertops to outdoor adventures." features={['Context placement', 'Human interaction', 'Seasonal themes', 'Location variety']} />
            <CapabilityCard icon={Palette} title="Brand Line Visuals" description="Campaign-ready compositions that align with your brand guidelines and color palette." features={['Brand alignment', 'Color matching', 'Composition styles', 'Campaign themes']} />
            <CapabilityCard icon={Globe} title="Environment Builds" description="Create custom sets, surreal worlds, or conceptual environments for your products." features={['Custom sets', 'Fantasy environments', 'Concept visualization', 'Scene building']} />
            <CapabilityCard icon={Clock} title="Digital-First Execution" description="No physical shoot required. Faster turnaround, lower costs, infinite possibilities." features={['2-week delivery', '70% cost savings', 'Unlimited revisions', 'Scalable output']} />
          </div>
        </div>
      </section>

      {/* Before/After Comparison */}
      <section className="py-24 md:py-32 bg-[#141414]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <p className="text-[#af0000] font-mono text-sm uppercase tracking-widest mb-4">See The Difference</p>
            <h2 className="text-4xl md:text-5xl font-bold font-['Syne'] tracking-tight">Static vs AI-Enhanced</h2>
          </div>

          <div data-testid="comparison-slider" className="relative max-w-4xl mx-auto aspect-[16/9] cursor-ew-resize overflow-hidden" onMouseMove={handleSliderChange}>
            <img src="https://images.unsplash.com/photo-1523275335684-37898b6baf30?w=1200&h=675&fit=crop" alt="AI Enhanced" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 overflow-hidden" style={{ width: `${sliderPosition}%` }}>
              <img src="https://images.unsplash.com/photo-1523275335684-37898b6baf30?w=1200&h=675&fit=crop&sat=-100" alt="Before" className="absolute inset-0 w-full h-full object-cover" style={{ filter: 'grayscale(100%) brightness(0.8)' }} />
            </div>
            <div className="absolute top-0 bottom-0 w-1 bg-[#af0000] cursor-ew-resize" style={{ left: `${sliderPosition}%` }}>
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-12 h-12 bg-[#af0000] rounded-full flex items-center justify-center shadow-lg">
                <ArrowRight size={20} className="text-white rotate-180" />
                <ArrowRight size={20} className="text-white" />
              </div>
            </div>
            <div className="absolute top-4 left-4 bg-black/50 px-3 py-1 text-sm font-semibold">Before</div>
            <div className="absolute top-4 right-4 bg-[#af0000] px-3 py-1 text-sm font-semibold">AI Enhanced</div>
          </div>
          <p className="text-center text-[#94a3b8] mt-6">Drag the slider to compare static product photography with AI-enhanced visuals</p>
        </div>
      </section>

      {/* Industries */}
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <p className="text-[#af0000] font-mono text-sm uppercase tracking-widest mb-4">Use Cases</p>
            <h2 className="text-4xl md:text-5xl font-bold font-['Syne'] tracking-tight">Industries we serve</h2>
          </div>
          <div className="flex flex-wrap justify-center gap-4">
            {['Fashion & Apparel', 'Beauty & Cosmetics', 'Food & Beverage', 'Electronics', 'Jewelry & Accessories', 'Home & Living', 'Sports & Outdoor', 'Luxury Goods'].map((industry, index) => (
              <span key={index} className="px-6 py-3 border border-white/20 text-[#f8fafc] hover:border-[#af0000] hover:bg-[#af0000]/10 transition-all cursor-default">{industry}</span>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-t from-[#af0000]/20 to-transparent" />
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center relative z-10">
          <h2 className="text-4xl md:text-6xl font-bold font-['Syne'] tracking-tight mb-6">
            Ready to revolutionize<br /><span className="text-[#af0000]">your product imagery?</span>
          </h2>
          <p className="text-xl text-[#94a3b8] mb-10 max-w-2xl mx-auto">Get a free visual sample and see what AI can do for your brand.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact" data-testid="shotlab-cta-sample-btn" className="btn-primary inline-flex items-center justify-center gap-2">Request Visual Sample <Sparkles size={18} /></Link>
            <Link to="/contact" data-testid="shotlab-cta-shoot-btn" className="btn-secondary inline-flex items-center justify-center gap-2">Start AI Shoot <ArrowUpRight size={18} /></Link>
          </div>
        </div>
      </section>
    </div>
  );
};

const StatCard = ({ value, label }) => (
  <div className="text-center">
    <div className="text-4xl md:text-5xl font-bold font-['Syne'] text-[#af0000] mb-2">{value}</div>
    <p className="text-[#94a3b8]">{label}</p>
  </div>
);

const CapabilityCard = ({ icon: Icon, title, description, features }) => (
  <div data-testid="capability-card" className="capability-card p-8 bg-white/5 border border-white/10 hover:border-[#af0000]/30 transition-all">
    <div className="w-12 h-12 bg-[#af0000]/20 flex items-center justify-center mb-6">
      <Icon size={24} className="text-[#af0000]" />
    </div>
    <h3 className="text-xl font-bold font-['Syne'] mb-3">{title}</h3>
    <p className="text-[#94a3b8] text-sm leading-relaxed mb-6">{description}</p>
    <ul className="space-y-2">
      {features.map((feature, idx) => (
        <li key={idx} className="flex items-center gap-2 text-sm">
          <CheckCircle size={14} className="text-[#af0000]" />
          <span className="text-[#f8fafc]">{feature}</span>
        </li>
      ))}
    </ul>
  </div>
);

export default ShotLab;
